/*
Prescription Mixin

- wanted to split out into separate files like cart mixins but it was being a pain and i dont have time
- if product is prescriptible and available, check for prescription data
    ↳ format prescription options for later on and set things up
    ↳ create option group key to correlate base product with option products
    ↳ add base product and option products to cart with the same group key
*/

import Vue from 'vue';
import axios from 'axios';
import productKeys from '../utils/productKeys.js';
import { getPrescriptionUrl, getPrescriptionUploadUrl } from '../utils/api';
 
const PRESCRIPTION_URL = getPrescriptionUrl();
const PRESCRIPTION_UPLOAD_URL = getPrescriptionUploadUrl(); 

export default function prescriptionController() {
    return {

        data() {
            return {
                // Prescription
                currencyRate: parseFloat(Shopify.currency.rate),
                prescriptionWorkflowVisible: false,
                prescriptionData: null,
                prescriptionUploadUrl: null,
                contactLensOptions: null,
                stepPopups: {},
                fileUpload: { // manage and keep track of all the variables for the file upload
                    'file': null, // file data - used for the upload
                    'error': false, // does it have an error - used for messaging
                    'message': '', // message content - the actual message for the user, if any
                    'success': false, // did the file upload successfully - enables/disables the Next button based on BE response
                    'valid': false, // is the file valid - enables/disables the upload button based on FE valdiation
                    'fileUrl': null, // url to the uploaded file
                    'fileName': null
                }
            }
        },
    
        computed: {
            /////////////////////////////////////
            // Prescription 
            prescriptionSelected() { // Collate all the selected options for easy referencing later on
                if (!this.prescriptionData || !this.prescriptionData.steps.length) return false;
                return this.prescriptionData.steps.map( elem => {
                    return elem.selected ? elem.options.filter( option => elem.selected.includes( option.option_code )) : false
                })
            },
            prescriptionSubFormValid() {
                let isValid = [
                        ...this.prescriptionSelected.map( step => {
                        let stepOutput = step.map( option => {
    
                            // override the values if it is the upload form, check the fileupload var instead
                            if (option.option_code === productKeys.UPLOAD_PRESCRIPTION_FIELD_CODE) {
                                return this.fileUpload.success;
                            } 
                            // skip if it is the email subscription option
                            else if (option.option_code === productKeys.EMAIL_PRESCRIPTION_FIELD_CODE) {
                                return null
                            }
                            // do some different logic if it is the Enter Prescription sub form
                            else if (option.option_code === 'enter_prescription') {

                                // if the option has no required children, we don't care about it
                                if (!option.selectedChildren.required) return null;

                                    // grab all the options related to pupillary distance...
                                let pupillaryDistanceKeys = Object.keys(option.selectedChildren.required).filter( optionKey => optionKey.toLowerCase().includes('pupillary')),
                                    // ... get their values ...
                                    pupillaryDistanceValues = pupillaryDistanceKeys.map( key => option.selectedChildren.required[key]),
                                    // ... check if at least one of them is true ...
                                    pupillaryDistanceOutput = !!pupillaryDistanceValues.length ? pupillaryDistanceValues.some(value => !!value) : null,
                                    // ... then get all the OTHER required options
                                    requiredOptionsKeys = Object.keys(option.selectedChildren.required).filter( optionKey => !optionKey.toLowerCase().includes('pupillary')),
                                    // ... then get all of their values ...
                                    requiredOptionsValues = requiredOptionsKeys.map( key => option.selectedChildren.required[key]);

                                // ... then append the pupillary distance value ...
                                requiredOptionsValues.push(pupillaryDistanceOutput);

                                // ..., then check they are all true and pass it back
                                return requiredOptionsValues.every( value => !!value )
                            }
                            // otherwise just check that every required field of the option's subform is not NULL
                            // if the option is not a form type, or has no subform, we don't care about it
                            else if (option.type == 'form' && !!option.selectedChildren) {
                                // if the option has no required children, we don't care about it
                                if (!option.selectedChildren.required) return null;
                                
                                // grab all the values and chuck it into an array for us to check
                                return Object.values(option.selectedChildren.required).map( value => value );
                            } else {
                                return null;
                            }
                        });

                        return stepOutput;
                    })
                ]
                // flatten one depth to bring everything onto the same level
                .flat(1)
                // now remove all the raw null values from the array - 
                // if stuff isn't in an array, then its not a subform and we don't care about it
                .filter( item => item !== null)
                // now flatten again to make the next step more straighforward
                .flat(1)
                // check every value is not NULL or FALSE
                .every( item => !!item );
    
                return isValid;
            },
            prescriptionSkipToEnd() {
                // Do any of the options include the flag that they need to skip to the end? (usually just Step 1, Option 3)
                return this.prescriptionSelected.filter( e => {
                    return e.find( e => e.should_skip_to_end);
                }).length > 0;
            },
    
            /////////////////////////////////////
            // General
            prescriptionSphereValues() {
                if (!this.prescriptionSelected) return false;
    
                let enterPrescriptionValues = this.prescriptionSelected.flat(1).find( option => option.option_code === productKeys.ENTER_PRESCRIPTION_FIELD_CODE);
                
                if (!!enterPrescriptionValues) {
                    let leftSph = !!enterPrescriptionValues ? enterPrescriptionValues.selectedChildren.required[productKeys.LEFT_SPH_FIELD_CODE] : false,
                        rightSph = !!enterPrescriptionValues ? enterPrescriptionValues.selectedChildren.required[productKeys.RIGHT_SPH_FIELD_CODE] : false,
                        leftSphNum = !!leftSph ? Math.abs(parseFloat(leftSph)) : 0,
                        rightSphNum = !!rightSph ? Math.abs(parseFloat(rightSph)) : 0,
                        highestVal = Math.max(leftSphNum, rightSphNum);
                    
                        return highestVal;
                } else {
                    return false;
                }
            }
    
        },

        watch: {
            prescriptionWorkflowVisible(value) {
                if (value) {
                    document.querySelector('body').classList.add('no-scroll');
                } else {
                    document.querySelector('body').classList.remove('no-scroll');
                }
            },
            prescriptionSphereValues(value) {
                // Automatically recommened the most appropriate thickness based on the select SPH values from the Enter Prescription step
    
                let formattedValue = parseFloat(`${value}`.replace('-', '')),
                    thicknessOptions = this.prescriptionData.steps.find( step => step.category === productKeys.LENS_THICKNESS_STEP_CATEGORY);
    
                if (!!thicknessOptions) {
                    let options = thicknessOptions.options;
    
                    // reset all recommendations
                    for (const option of options) {
                        option.is_recommended = false;
                    };
    
                    if (!!value) {

                        /*
                            - SHP 0 to 1.5 = 1.5 thickness
                            - SHP 1.75 to 2 = 1.56 thickness
                            - SHP 2.25 to 3 = 1.6 thickness
                            - SHP 3.25 to 4 = 1.67 thickness (if present, otherwise 1.6)
                            - SHP 4.25 to 12 = 1.71 thickness (if present, otherwise 1.6)

                            - Sphere power (SPH) of up to +/-1.50 = 1.5 index 
                            - Sphere power (SPH) of up to +/-2.00 = 1.56 index
                            - Sphere power (SPH) of up to +/-3.00 = 1.6 index
                            - Sphere power (SPH) of up to +/-4.00 = 1.67 index
                            - Sphere power (SPH) of +/-4.00 and above = 1.71 index

                            Now, in the scenario where a user has selected one eye as -0.75 and the other eye as -3.00, then in this case the recommendation needs to be 1.6 index and not 1.5 
                            But if the user selects -0.75 and +1.25, the recommendation can be 1.5 because both eyes' SPH fall under the same range to recommend 1.5 correctly. 
                            Please note - the logic fix must apply to existing options and any options we create for new templates. 
                            In the case of sunglasses, the maximum lens index we can cater for as of now is 1.6, however, in the future if we manage to cater for more indexes, then we should be able to add a new option to respective templates and the logic must work as explained above. 
                        */
                        
                        
                        if (formattedValue <= 1.5) {
                            let option = options.find( option => option.option_code.includes(productKeys.LENS_THICKNESS_CODE_15));
                            if (!!option) option.is_recommended = true;
                        }
                        else if (formattedValue > 1.5 && formattedValue <= 2) {
                            let option = options.find( option => option.option_code.includes(productKeys.LENS_THICKNESS_CODE_156));
                            if (!!option) option.is_recommended = true;
                        }
                        else if (formattedValue > 2 && formattedValue <= 3) {
                            let option = options.find( option => option.option_code.includes(productKeys.LENS_THICKNESS_CODE_16));
                            if (!!option) option.is_recommended = true;
                        }
                        else if (formattedValue > 3 && formattedValue <= 4) {
                            let option = options.find( option => option.option_code.includes(productKeys.LENS_THICKNESS_CODE_167));
                            if (!option) {
                                option = options.find( option => option.option_code.includes(productKeys.LENS_THICKNESS_CODE_16));
                            };
                            if (!!option) option.is_recommended = true;
                        }
                        else if (formattedValue > 4) {
                            let option = options.find( option => option.option_code.includes(productKeys.LENS_THICKNESS_CODE_174));
                            if (!option) {
                                option = options.find( option => option.option_code.includes(productKeys.LENS_THICKNESS_CODE_171));
                                if (!option) {
                                    option = options.find( option => option.option_code.includes(productKeys.LENS_THICKNESS_CODE_16));
                                }
                            }
                            if (!!option) option.is_recommended = true;
                        }
                    }
                }
            }
        },

        methods: {
            convertPrice(price) {
                return Math.floor(price * this.currencyRate)
            },
            ///////////////////////////
            // Prescription Popup/Workflow Controls
            clearSelectedPrescription(val) {

                // If prescription is not enabled then skip
                if (!!this.prescriptionData) {
                    if (val === 'all') {
                        this.prescriptionData.steps.forEach( e => {
                            e.selected = [];
                        })
                    } else {
                        this.prescriptionData.steps[val].selected = [];
                    }
                }
            },
            togglePrescriptionWorkflow() {
                this.prescriptionData.currentStep = 1;
                this.prescriptionWorkflowVisible = !this.prescriptionWorkflowVisible;

                if (!this.prescriptionWorkflowVisible) this.clearSelectedPrescription('all');
            },
            prescriptionWorkflowPrevious() {
                let newPage = null;

                if (this.prescriptionSkipToEnd) {
                    newPage = 1;
                } else {
                    // set the new page
                    newPage = this.prescriptionData.currentStep -= 1;

                    if (newPage <= 0)  {
                        newPage = 1;
                        this.togglePrescriptionWorkflow();
                    } else {
                        // if its not 0, which is closing and clearing completely, then reset the previously selected step data
                        // passing the newPage val since the steps array is 0 indexed
                        this.clearSelectedPrescription(newPage);
                    }
                }

                if (newPage === 1) {
                    this.fileUpload.file = null;
                    this.fileUpload.error = false;
                    this.fileUpload.message = '';
                    this.fileUpload.fileUrl = null;
                    this.fileUpload.fileName = null;
                }
                
                this.prescriptionData.currentStep = newPage;
                document.querySelector('.product-prescription__steps').scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            },
            prescriptionWorkflowNext() {
                let newPage = null;

                if (this.prescriptionSkipToEnd) {
                    newPage = this.prescriptionData.stepsCount;
                }
                else {
                    newPage = this.prescriptionData.currentStep += 1;

                    if (newPage >= this.prescriptionData.stepsCount) {
                        newPage = this.prescriptionData.stepsCount;
                    }
                }
                this.prescriptionData.currentStep = newPage;
                    document.querySelector('.product-prescription__steps').scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
            },
            prescriptionScrollToContent() {
                // Needs to be visible so we actually have content to scroll to
                if (this.prescriptionWorkflowVisible && this.prescriptionData.currentStep > 0) {
                    let visibleHeight = window.innerHeight - document.querySelector('.product-prescription__summary').offsetHeight,
                        elemOffset = document.querySelector('.product-prescription__step-footer').offsetTop;


                        if (elemOffset > visibleHeight) {
                            document.querySelector('.product-prescription__steps').scrollTo({
                                top: elemOffset,
                                behavior: 'smooth'
                            });
                        }
                }
            },

            ///////////////////////////
            // Field specific functionality
            prescriptionFileUploadFieldController(event) {
                // On file change reset everything
                this.fileUpload.file = null;
                this.fileUpload.error = false;
                this.fileUpload.message = '';
                this.fileUpload.valid = false;
                this.fileUpload.fileUrl = null;
                this.fileUpload.fileName = null;
            
                // Grab the file
                let file = event.target.files[0];
        
                // Check if there even is a file, if not we can't do anything, it will just break and throw errors
                if (!!file) {

                    let type = file.type.split('/')[1],
                        size = Math.round(file.size / 1024);

                    // Validate everything first
                    // check file type is one of the accepted types
                    if (type !== 'png' &&
                        type !== 'gif' &&
                        type !== 'jpeg' &&
                        type !== 'jpg' &&
                        type !== 'pdf') {
                    this.fileUpload.error = true;
                    this.fileUpload.message = this.messageUploadType;  
                    } 
                    // check if file size is over 2mb
                    else if (size > 2048) {
                        this.fileUpload.error = true;
                        this.fileUpload.message = this.messageUploadSize;
                    } else {
                        // otherwise, if everything is good, keep the file for later and update the fields add as line item prop

                        // Need to create a new File object so that we can add a unique name. File Object API has NAME as a read only value
                        this.fileUpload.file = new File(
                            // Yoink out the file data and whack it into the new file
                            [file],

                            // Add a unique name to prevent things being overwritten on the server
                            `${Math.floor(Math.random() * Math.floor(Math.random() * Date.now()))}_${file.name}`,

                            // Pop in the existing file options
                            { 'type': file.type }
                        );
                        this.fileUpload.valid = true;

                        // store some data for building line item props later
                        this.prescriptionSelected[1][0].selectedChildren.required.upload_prescription = file.name
                    }
                }
            },

            ///////////////////////////
            // Data handling
            prescriptionFileUpload() {
                // let uploadUrl = this.$refs.file_upload[0].dataset.url,
                let formData = new FormData();

                formData.append('file', this.fileUpload.file);

                axios.post( PRESCRIPTION_UPLOAD_URL, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
                .then( response => {
                    if (!response.data.success || response.data.success === 'false') {
                        this.fileUpload.error = true;
                        this.fileUpload.message = response.data.message;
                        this.fileUpload.valid = false;
                    } else if (response.data.success || response.data.success === 'true'){
                        this.fileUpload.error = false;
                        this.fileUpload.message = this.messageUploadSuccess;
                        this.fileUpload.success = true;

                        this.prescriptionSelected[1][0].selectedChildren.required.upload_prescription  += `|||${response.data.file_name}`;
                        this.fileUpload.fileUrl = response.data.file_url;
                        this.fileUpload.fileName = response.data.file_name;
                    }
                })
                .catch( error => {
                    console.error("UPLOAD ERROR", error);

                    this.fileUpload.error = true;
                    this.fileUpload.message = `
                        ${error}
                        <br />
                        ${this.messageUploadError}
                    `;
                    this.fileUpload.valid = false;
                })
            },
            _getPrescriptionData(variantId) {
                let prescriptionUrl = `${PRESCRIPTION_URL}${variantId}`;

                return axios.get(prescriptionUrl).then((response) => {
                        
                        // Need to reformat all the Prescription Data into a format that we like.
                        // Its kind of gross and obtuse, soz. This could get cleaned up and simplified,
                        // but it will be better to talk to the BE dev first to see if we can adjust the 
                        // response data to get it closer to what we want
            
                        let steps = response.data.prescriptions.steps.map( (step, index) => {
            
                            let messagePopup = { popup: false, popupData: {} };
                            let hasLink = step.description.includes('<a href');
                            if (!!hasLink) {
                                let parsedString = new DOMParser().parseFromString(step.description, 'text/xml');
                                let parsedATag = parsedString.querySelector('a');
                                if (!!parsedATag) {
                                    let aTagHrefValue = parsedATag.attributes.href.value;
                                    let aTagLinkIsImage = /.(png|jpg|gif)/g.test(aTagHrefValue);
            
                                    if (!!aTagLinkIsImage) {
                                        let aTagInnerText = parsedATag.innerHTML,
                                            key = 'popup-step-' + index;
                                        messagePopup.popup = true;
                                        messagePopup.key = key;
                                        messagePopup.popupData.link = aTagHrefValue;
                                        messagePopup.popupData.text = aTagInnerText
            
                                        Vue.set(this.stepPopups, key, false);
                                    }
                                }
                            }
            
                            return {
                                ...step,
                                ...messagePopup,
                                // Default selected to array, so it works for checkboxes and radio options
                                "selected": [],
                                "options": step.options.map( option => {
                                    if (option.type === 'form') {
            
                                        return {
                                            ...option,
                                            "options": {
                                                ...option.options,
                                                
                                                // If object wrap in array
                                                "fields": option.options.fields,
                                            },
                                            // set container for selected child fields. preset values so it works happily
                                            "selectedChildren": (
                                                // need to check first if it is an array or not and make sure we pass an array
                                                !Array.isArray(option.options.fields) ? [option.options.fields] : option.options.fields
                                            ).reduce( (acc, val) => {
                                                let temp = { ...acc };
                                                if (val.required) {
                                                    temp.required[val.code] = null;
                                                } else {
                                                    temp.other[val.code] = null;
                                                }
                                                return { ...temp }
                                            }, {"required": {},"other": {}} )
                                        }
                                    } else {
                                        return option
                                    }
                                })
                            }
                        });
            
                        this.prescriptionData =  {
                            steps: steps,
                            stepsCount: Object.keys(steps).length,
                            currentStep: 1
                        };
            
                        if (!!this.prescriptionData && !!this.isContactLens) {
                            this._generateContactLensOptions();
                        }
            
                }).catch((error) => {
                    console.log(error);
                });
            },
        }
    }
}